import { apiGet, apiPut, apiDelete } from './client';
import type { UserCustomizationSettings } from '../types/UserCustomization';
import { emptyCustomization } from '../types/UserCustomization';

interface ServerDto {
  resourceCode: string;
  settingsJson: string;
}

// 204 = "no customization yet for this user/resource" — return null.
// Otherwise parse the JSON blob the backend stored. Defensive: if the JSON is
// malformed (e.g. an older version we no longer support), fall back to empty
// rather than break the page.
export async function getCustomization(
  resourceCode: string,
): Promise<UserCustomizationSettings | null> {
  try {
    const dto = await apiGet<ServerDto>(
      `/api/user-customizations/${encodeURIComponent(resourceCode)}`,
    );
    if (!dto || !dto.settingsJson) return null;
    try {
      return JSON.parse(dto.settingsJson) as UserCustomizationSettings;
    } catch {
      return emptyCustomization();
    }
  } catch (err: unknown) {
    // apiGet calls res.json() on an empty 204 body which throws SyntaxError;
    // we catch that here as the "no customization" signal.
    if (err instanceof SyntaxError) return null;
    throw err;
  }
}

export async function saveCustomization(
  resourceCode: string,
  settings: UserCustomizationSettings,
): Promise<void> {
  await apiPut<{ settingsJson: string }, void>(
    `/api/user-customizations/${encodeURIComponent(resourceCode)}`,
    { settingsJson: JSON.stringify(settings) },
  );
}

export async function resetCustomization(resourceCode: string): Promise<void> {
  await apiDelete(`/api/user-customizations/${encodeURIComponent(resourceCode)}`);
}
